import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash';
import { compose } from 'redux';

import { withShowHide } from '@sparefoot/react-core/decorators';
import { formatUSD } from '@sparefoot/react-core/utils/format';
import { ViewportAction } from '@sparefoot/react-core';

import { withOnVisibleFacilityHandler } from 'decorators/facility';
import { withSelfStorageSchema } from 'decorators/search';
import { Link } from 'components/core/Link';
import { H3, Text, Paragraph } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { FacilityImage } from 'components/facility/FacilityImage';
import { FacilityRating } from 'components/facility/FacilityRating';
import { FacilityRatingLabel } from 'components/facility/FacilityRatingLabel';
import { OONFacilityCard } from 'components/facility/OONFacilityCard';
import { ClickToCall } from 'components/facility/ClickToCall';
import { StackedUnits } from 'components/unit/StackedUnits';
import { ExpressBanner } from 'components/core/ExpressBanner';

import PhotosIcon from 'assets/images/icons/photos.svg';
import ClockIcon from 'assets/images/icons/clock.svg';

import './FacilityCard.scss';

export const enhance = compose(
	withShowHide('review'),
	withOnVisibleFacilityHandler,
	withSelfStorageSchema,
);

export function FacilityCard({
	facility,
	getRef: getRefProp,
	getFacilityCardPhoneNumber,
	showRecommendedUnit,
	storageType = 'storage',
	withUnits,
	responsive = true,
	ratingRenderLabel = FacilityRatingLabel,
	onMouseOverRating = noop,
	onShowReview: onShowReviewProp,
	onHideReview,
	onVisible,
	reviewShowing,
	percentVisible,
	selfStorageSchema,
	withSchema,
	baseUrl,
}) {
	const getRef = useCallback(
		(instance) => getRefProp?.(facility.id, instance),
		[getRefProp, facility.id],
	);

	const handleFetchNumber = useCallback(
		() => getFacilityCardPhoneNumber(),
		[getFacilityCardPhoneNumber],
	);

	const onShowReview = useCallback(() => {
		onMouseOverRating(facility.id);
		onShowReviewProp();
	}, [onMouseOverRating, facility.id, onShowReviewProp]);

	if (!facility.inNetwork) {
		return (
			<OONFacilityCard
				facility={facility}
				getRef={getRefProp}
				getFacilityCardPhoneNumber={getFacilityCardPhoneNumber}
				showRecommendedUnit={showRecommendedUnit}
				storageType={storageType}
				withUnits={withUnits}
				responsive={responsive}
				ratingRenderLabel={ratingRenderLabel}
				onMouseOverRating={onMouseOverRating}
				onShowReview={onShowReviewProp}
				onHideReview={onHideReview}
				onVisible={onVisible}
				reviewShowing={reviewShowing}
				percentVisible={percentVisible}
				selfStorageSchema={selfStorageSchema}
				withSchema={withSchema}
				baseUrl={baseUrl}
			/>
		);
	}

	return (
		<ViewportAction
			className={classnames('facility-card', {
				'no-units': !withUnits,
				responsive: responsive && withUnits,
			})}
			onVisible={onVisible}
			percentVisible={percentVisible}
		>
			<div
				ref={getRef}
				itemScope
				className="facility-card-content"
			>
				<Link
					className="facility-photo"
					href={`${baseUrl}${facility.url}`}
				>
					<div
						className="image-wrapper"
						itemProp="image"
						itemScope
						itemType="https://schema.org/ImageObject"
					>
						<FacilityImage
							itemProp="url"
							src={facility.images[0]}
							alt={`Photo of ${facility.name}`}
							size="medium"
							lazyLoad={true}
						/>
					</div>
					<span className="omi-photos-text">
						{facility.images.length > 1 && (
							<span className="omi-more-photos">
								<Image
									src={PhotosIcon}
									alt="Photos Icon"
									aria-hidden="true"
								/>
								{facility.images.length} Photos
							</span>
						)}
						{facility.isOpen && (
							<span className="omi-open-now">
								<Image
									src={ClockIcon}
									alt="Clock Icon"
									aria-hidden="true"
								/>
								Open
							</span>
						)}
					</span>
				</Link>
				<div className="facility-details">
					<div className="cl-address">
						<div className="cl-address">
							<H3
								className="facility-name"
								size="small"
								last
							>
								<Link
									className="facility-link"
									href={`${baseUrl}${facility.url}`}
								>
									<span itemProp="name">{facility.name}</span>
								</Link>
							</H3>
							<div className="facility-location">
								<Text
									itemProp="address"
									itemScope
									itemType="https://schema.org/PostalAddress"
									className={classnames(
										'facility-address',
										'omi-facility-address',
									)}
								>
									<span
										itemProp="streetAddress"
										className="street street-address visually-hidden"
									>
										{facility.location.streetAddress1}
										{!!facility.location.streetAddress2 &&
											`, ${facility.location.streetAddress2}`}
									</span>
									<span
										itemProp="addressLocality"
										className="city locality"
									>
										{facility.location.city}
									</span>{' '}
									<span
										itemProp="addressRegion"
										className="state region"
									>
										{facility.location.state}
									</span>{' '}
									<span
										itemProp="postalCode"
										className="zip postal-code"
									>
										{facility.location.zip}
									</span>
								</Text>
								<Text
									className={classnames(
										'facility-distance',
										'omi-facility-distance',
									)}
								>
									{facility.distance} miles away
								</Text>
							</div>
						</div>
						{facility.hasOnlineMoveins && (
							<div className="express-banner">
								<ExpressBanner />
							</div>
						)}
						<div className="omi-phone-n-reviews">
							<ClickToCall
								phone={facility.phone}
								onFetchNumber={handleFetchNumber}
							/>
							{!!facility.reviews.number && (
								<FacilityRating
									numberOfRatings={facility.reviews.number}
									average={facility.reviews.average}
									reviewsUrl={`${facility.url}#facility-page-user-reviews`}
									renderLabel={ratingRenderLabel}
									onMouseOver={onShowReview}
									onMouseOut={onHideReview}
									reviewShowing={reviewShowing}
									review={facility.highestReview}
									reviewTitle={`What customers are saying about ${facility.name}`}
								/>
							)}
						</div>
						{!!facility.lowestPrice && (
							<Paragraph
								className="facility-lowest-price"
								last
							>
								Starting at{' '}
								<Link
									href={`${baseUrl}${facility.url}`}
									className="starting-at-link"
								>
									{formatUSD(facility.lowestPrice)}
								</Link>
							</Paragraph>
						)}
					</div>
				</div>
				{!!withUnits && (
					<StackedUnits
						facility={facility}
						units={facility.units}
						storageType={storageType}
						showRecommendedUnit={showRecommendedUnit}
						withScarcity
						className="omi-stacked-units"
					/>
				)}
			</div>
			{withSchema && (
				<script
					key="schema"
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			)}
		</ViewportAction>
	);
}
FacilityCard.propTypes = {
	reviewShowing: PropTypes.bool,
	facility: PropTypes.object,
	getRef: PropTypes.func,
	getFacilityCardPhoneNumber: PropTypes.func,
	ratingRenderLabel: PropTypes.func,
	onMouseOverRating: PropTypes.func,
	onShowReview: PropTypes.func,
	onHideReview: PropTypes.func,
	onVisible: PropTypes.func.isRequired,
	showRecommendedUnit: PropTypes.bool,
	storageType: PropTypes.oneOf(['storage', 'vehicle']),
	withUnits: PropTypes.bool,
	responsive: PropTypes.bool,
	percentVisible: PropTypes.number,
	selfStorageSchema: PropTypes.string,
	withSchema: PropTypes.bool,
	baseUrl: PropTypes.string,
};

export default enhance(FacilityCard);
